import { computed, Pipe, PipeTransform, signal, untracked } from '@angular/core';
import {
  format as fnsFormat,
  isDate as fnsIsDate,
  isSameDay,
  isSameHour,
  isSameMinute,
  isSameMonth,
  isSameSecond,
  isSameYear,
} from 'date-fns';
import { cleanFormat } from '../../functions/format/clean-format.function';
import { containsDateFormat } from '../../functions/format/contains-date-format.function';
import { containsTimeFormat } from '../../functions/format/contains-time-format.function';
import { removeDateFromFormat } from '../../functions/format/remove-date-from-format.function';
import { removeMonthFromFormat } from '../../functions/format/remove-month-from-format.function';
import { removeYearFromFormat } from '../../functions/format/remove-year-from-format.function';
import { isDateRangeInterface } from '../../functions/is-date-range.function';
import { DateRange } from '../../models/date-range/date-range.model';
import { TsRange } from '../../models/ts-range/ts-range.model';
import { DateRangePipeOptions } from './date-range-pipe-options.interface';
import { parseRawDate } from './parse-raw-date.function';

/**
 * A pipe that formats dates and date ranges with intelligent shortening logic.
 *
 * It supports various input types including `Date`, `TsRange`, `DateRange`, and ISO strings.
 * It can automatically shorten ranges by removing redundant information (e.g., same day, same month, or same year).
 *
 * @example
 * {{ dateRange | ttDate }} // "01 Jan 2024 10:00 - 11:00" (if same day)
 * {{ dateRange | ttDate:{ representation: 'date' } }} // "01 - 05 Jan 2024" (if same month)
 */
@Pipe({
  name: 'ttDate',
  standalone: true,
})
export class TtDateRangePipe implements PipeTransform {
  private readonly value = signal<TsRange | DateRange | Date | null>(null);

  private readonly isDate = computed(() => {
    return fnsIsDate(this.value());
  });

  private readonly isRange = computed(() => {
    return !this.isDate();
  });

  private readonly shortenCurrentYear = signal(true);

  private readonly shorten = signal(true);

  private readonly infiniteNotation = signal('');

  private readonly ignoreBoundaries = signal(false);

  private readonly locale = signal<DateRangePipeOptions['locale']>(undefined);

  private readonly divider = signal(' - ');

  private readonly shortenToOnlyStartDate = computed(() => {
    if (this.isDateTimeFormat()) {
      return this.shortenEndDateBecauseSame() && this.shortenTimeBecauseSame();
    }
    if (this.isTimeFormat()) {
      return this.shortenTimeBecauseSame();
    }
    if (this.isDateFormat()) {
      return this.shortenEndDateBecauseSame();
    }
    return false;
  });

  private readonly representation = signal<'complete' | 'date' | 'time'>('complete');

  private readonly givenFormat = signal<string | undefined>(undefined);

  private readonly format = computed(() => {
    const givenFormat = this.givenFormat();
    let newFormat: string;
    switch (true) {
      case !!givenFormat:
        newFormat = givenFormat;
        break;
      case this.representation() === 'date' || this.value() instanceof DateRange:
        newFormat = 'dd MMMM y';
        break;
      case this.representation() === 'time':
        newFormat = 'HH:mm';
        break;
      default:
        newFormat = 'dd MMMM yyyy HH:mm';
    }

    if (this.shortenCurrentYear() && !this.ignoreBoundaries()) {
      return this.stripCurrentYearFromFormat(newFormat);
    }
    return newFormat;
  });

  private readonly isDateTimeFormat = computed(() => {
    return containsDateFormat(this.format()) && containsTimeFormat(this.format());
  });

  private readonly isTimeFormat = computed(() => {
    return !containsDateFormat(this.format()) && containsTimeFormat(this.format());
  });

  private readonly isDateFormat = computed(() => {
    return containsDateFormat(this.format()) && !containsTimeFormat(this.format());
  });

  private readonly startFormat = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    const format = this.format();

    if (
      this.shorten() &&
      !this.shortenToOnlyStartDate() &&
      this.isRange() &&
      this.isDateFormat() &&
      start &&
      end
    ) {
      if (isSameMonth(start, end) && isSameYear(start, end)) {
        return cleanFormat(removeYearFromFormat(removeMonthFromFormat(format)));
      }
      if (isSameYear(start, end)) {
        return cleanFormat(removeYearFromFormat(format));
      }
    }
    return format;
  });

  private readonly endFormat = computed(() => {
    const format = this.format();
    if (this.isDateTimeFormat() && this.shortenEndDateBecauseSame()) {
      return removeDateFromFormat(format);
    }
    return format;
  });

  private readonly startDate = computed(() => {
    const value = this.value();
    if (!value || fnsIsDate(value)) {
      return value;
    }
    return this.ignoreBoundaries() ? value.start : value.min;
  });

  private readonly startDateStringify = computed(() => {
    const startDate = this.startDate();
    if (!startDate) {
      return this.infiniteNotation();
    }
    return fnsFormat(startDate, this.startFormat(), { locale: this.locale() });
  });

  private readonly endDate = computed(() => {
    const value = this.value();
    if (!value || fnsIsDate(value)) {
      return value;
    }
    return this.ignoreBoundaries() ? value.end : value.max;
  });

  private readonly endDateStringify = computed(() => {
    const endDate = this.endDate();
    if (!endDate) {
      return this.infiniteNotation();
    }
    return fnsFormat(endDate, this.endFormat(), { locale: this.locale() });
  });

  private readonly shortenTimeBecauseSame = computed((): boolean => {
    const start = this.startDate();
    const end = this.endDate();
    if (!this.shorten() || !start || !end) {
      return false;
    }

    if (this.format().match(/[sS]+/)) {
      return isSameSecond(start, end);
    }
    if (this.format().match(/[m]+/)) {
      return isSameMinute(start, end);
    }
    if (this.format().match(/[hH]+/)) {
      return isSameHour(start, end);
    }
    return false;
  });

  private readonly shortenEndDateBecauseSame = computed((): boolean => {
    const start = this.startDate();
    const end = this.endDate();
    if (!this.shorten() || !start || !end) {
      return false;
    }
    if (this.format().match(/[dDEe]+/)) {
      return isSameDay(start, end);
    }
    if (this.format().match(/[ML]+/)) {
      return isSameMonth(start, end);
    }
    if (this.format().match(/[yY]+/)) {
      return isSameYear(start, end);
    }
    return false;
  });

  private result = computed(() => {
    if (this.isDate() || this.shortenToOnlyStartDate()) {
      return this.startDateStringify();
    }
    return `${this.startDateStringify()}${this.divider()}${this.endDateStringify()}`;
  });

  public transform<V extends Parameters<typeof parseRawDate>[0]>(
    value: V | null | undefined,
    {
      divider = ' - ',
      locale,
      referenceDate = new Date(),
      ignoreBoundaries = false,
      shorten = true,
      shortenCurrentYear = true,
      representation = isDateRangeInterface(value) ? 'date' : 'complete',
      format,
      originFormat = format,
      infiniteNotation = '',
    }: DateRangePipeOptions = {},
  ): V extends null | undefined ? null : string {
    if (!value) {
      return null as V extends null | undefined ? null : string;
    }

    untracked(() => {
      this.shortenCurrentYear.set(shortenCurrentYear);
      this.locale.set(locale);
      this.shorten.set(shorten);
      this.divider.set(divider);
      this.infiniteNotation.set(infiniteNotation);
      this.ignoreBoundaries.set(ignoreBoundaries);
      this.representation.set(representation);
      this.value.set(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parseRawDate(value as any, originFormat, referenceDate),
      );
      this.givenFormat.set(format);
    });

    return this.result() as V extends null | undefined ? null : string;
  }

  private stripCurrentYearFromFormat(format: string): string {
    const value = this.value();

    if (!format.match(/[yY]+/) || !value || !format.match(/[^yY]+/)) {
      return format;
    }

    const isRangeInCurrentYear =
      !fnsIsDate(value) &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((value.start && isSameYear(value.start, new Date())) || !value.start) &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((value.end && isSameYear(value.end, new Date())) || !value.end);
    const isDateInCurrentYear = fnsIsDate(value) && isSameYear(value, new Date());

    if (isRangeInCurrentYear || isDateInCurrentYear) {
      return cleanFormat(format.replace(/([yY])+/g, ''));
    }
    return format;
  }
}
